import { ref } from 'vue';
import kanbanAPI from 'dashboard/api/kanban';
import kanbanCardsAPI from 'dashboard/api/kanbanCards';
import { useRoute } from 'vue-router';

export function useKanban() {
  const route = useRoute();
  const loading = ref(false);
  const error = ref(null);
  const pipeline = ref(null);
  const columns = ref([]);

  const withState = async fn => {
    try {
      loading.value = true;
      error.value = null;
      return await fn();
    } catch (err) {
      error.value = err;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const fetchPipelines = params =>
    withState(async () => {
      const { data } = await kanbanAPI.getPipelines(params);
      return data;
    }).catch(err => {
      if (err?.response?.status === 404) {
        return [];
      }
      throw err;
    });

  const fetchPipeline = async pipelineId =>
    withState(async () => {
      const { data } = await kanbanAPI.getPipeline(pipelineId);
      pipeline.value = data;
      return data;
    }).catch(err => {
      if (err?.response?.status === 404) {
        pipeline.value = null;
        return null;
      }
      throw err;
    });

  const fetchColumns = async (pipelineId, params = {}) =>
    withState(async () => {
      const { data } = await kanbanAPI.getColumns(pipelineId, params);
      columns.value = data;
      return data;
    }).catch(err => {
      if (err?.response?.status === 404) {
        columns.value = [];
        return [];
      }
      throw err;
    });

  const loadMoreConversations = async (pipelineId, page) => {
    try {
      loading.value = true;
      const { data } = await kanbanAPI.getColumns(pipelineId, { page });
      return data;
    } catch (err) {
      error.value = err;
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const loadPipelineBoard = async pipelineId => {
    const id = pipelineId || route.params.pipelineId;
    if (!id) {
      pipeline.value = null;
      columns.value = [];
      return { pipeline: null, columns: [] };
    }
    await fetchPipeline(id);
    if (!pipeline.value) {
      columns.value = [];
      return { pipeline: null, columns: [] };
    }
    await fetchColumns(id);
    return { pipeline: pipeline.value, columns: columns.value };
  };

  const moveConversation = async (pipelineId, columnId, payload) => {
    // Não usar withState para evitar loading global
    try {
      await kanbanAPI.moveConversation(pipelineId, columnId, payload);
    } catch (err) {
      throw err;
    }
  };

  const removeConversationFromPipeline = (
    pipelineId,
    columnId,
    conversationId
  ) =>
    withState(async () => {
      await kanbanAPI.removeConversationFromPipeline(
        pipelineId,
        columnId,
        conversationId
      );
    });

  const createPipeline = payload =>
    withState(async () => {
      const { data } = await kanbanAPI.createPipeline(payload);
      return data;
    });

  const updatePipeline = (pipelineId, payload) =>
    withState(async () => {
      const { data } = await kanbanAPI.updatePipeline(pipelineId, payload);
      return data;
    });

  const deletePipeline = pipelineId =>
    withState(async () => {
      await kanbanAPI.deletePipeline(pipelineId);
    });

  const createColumn = (pipelineId, payload) =>
    withState(async () => {
      const { data } = await kanbanAPI.createColumn(pipelineId, {
        column: payload,
      });
      return data;
    });

  const updateColumn = (pipelineId, columnId, payload) =>
    withState(async () => {
      const { data } = await kanbanAPI.updateColumn(
        pipelineId,
        columnId,
        payload
      );
      return data;
    });

  const deleteColumn = (pipelineId, columnId) =>
    withState(async () => {
      await kanbanAPI.deleteColumn(pipelineId, columnId);
    });

  // KanbanCards functions
  const fetchCards = async pipelineId =>
    withState(async () => {
      const { data } = await kanbanCardsAPI.getCards(pipelineId);
      return data;
    });

  const createCard = (pipelineId, payload) =>
    withState(async () => {
      const { data } = await kanbanCardsAPI.createCard(pipelineId, payload);
      return data;
    });

  const moveCard = (pipelineId, cardId, payload) =>
    withState(async () => {
      await kanbanCardsAPI.moveCard(pipelineId, cardId, payload);
    });

  const getCardActivities = (pipelineId, cardId, params = {}) =>
    withState(async () => {
      const { data } = await kanbanCardsAPI.getCardActivities(
        pipelineId,
        cardId,
        params
      );
      return data;
    });

  return {
    route,
    loading,
    error,
    pipeline,
    columns,
    fetchPipelines,
    fetchPipeline,
    fetchColumns,
    loadPipelineBoard,
    loadMoreConversations,
    moveConversation,
    removeConversationFromPipeline,
    createPipeline,
    updatePipeline,
    deletePipeline,
    createColumn,
    updateColumn,
    deleteColumn,
    // KanbanCards
    fetchCards,
    createCard,
    moveCard,
    getCardActivities,
  };
}
